import { clearAuthSession, setAuthSessionItem } from "utils/authSession";
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  LuBuilding2,
  LuCalculator,
  LuChevronsUpDown,
  LuCircleCheck,
  LuCheck,
  LuCircleHelp,
  LuClock,
  LuClockAlert,
  LuCoins,
  LuCreditCard,
  LuEye,
  LuFileText,
  LuInfo,
  LuLayoutDashboard,
  LuLink,
  LuLock,
  LuLogOut,
  LuMenu,
  LuMoon,
  LuPackage,
  LuPanelLeftClose,
  LuPlus,
  LuReceiptText,
  LuSettings,
  LuSun,
  LuUser,
  LuUsers,
  LuWallet,
  LuX,
} from "react-icons/lu";
import { setDarkMode, useMaterialUIController, setMiniSidenav } from "context";
import {
  getBUDashboardInvoicesOwedToYouGraphDetails,
  getBusinessUserDasboardLineChartDataOrderRevenue,
  getBusinessUserDashboardCardData,
} from "utils/api";
import { matchesNavigationPath } from "utils/navigationMatch";
import { formatCurrencyAUD } from "utils/formatCurrency";
import { getInvoiceStatusKey } from "utils/invoiceStatus";
import calculateRoleRights, { screenRight } from "utils/roleRightPermissions";
import { menuAction } from "ReduxReducers/menuReducer";
import { appConstants } from "utils/appConstants";
import { getCachedRequest, setCachedRequest } from "utils/requestCache";
import AppHeader from "components/AppHeader";
import Skeleton from "components/Skeleton/Skeleton";
import CreateInvoiceModal from "layouts/Invoices/CreateInvoiceModal";
import OrganisationCreateDialog from "components/OrganisationCreateDialog";
import { showDraftSavedMessage } from "components/InlineFeedback/DraftSaveConfirmation";
import {
  fetchOrganisationContext,
  switchOrganisation,
} from "utils/organisationAPI";
import "./dashboard.css";

const NAVIGATION_SCROLL_STORAGE_KEY = "DASHBOARD_NAV_SCROLL_TOP";
const ORGANISATION_SWITCH_STORAGE_KEY = "INR_ORGANISATION_SWITCH_TARGET";
let retainedOrganisationContext = null;
let retainedOrganisationUserID = "";

const readOrganisationSwitchTarget = () => {
  try {
    return sessionStorage.getItem(ORGANISATION_SWITCH_STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

const readNavigationScrollTop = () => {
  try {
    const value = Number(sessionStorage.getItem(NAVIGATION_SCROLL_STORAGE_KEY));
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
};

const NAV_GROUPS = [
  [
    "SELL",
    [
      [LuFileText, "Quotations", "/quotations", "quotations"],
      [LuReceiptText, "Invoices", "/invoices", "invoices", "invoice"],
      [LuCreditCard, "Credit note", "/creditNote", "credit"],
    ],
  ],
  [
    "BUY",
    [
      [LuCalculator, "RCTI", "/rcti", "RCTI"],
      [LuFileText, "RCTI Agreements", "/rcti-agreements", "RCTI"],
      [LuWallet, "Expenses", "/expenses", "expenses"],
      [LuCreditCard, "Debit note", "/debitNote", "debit"],
    ],
  ],
  ["RECOVER", [[LuClockAlert, "Debt collection", "/debts", "debts", "debt"]]],
  [
    "MANAGE",
    [
      [LuUsers, "Contacts", "/contacts", "contacts"],
      [LuPackage, "Items", "/items", "items"],
      [LuCalculator, "Reports", "/invoiceReport", "invoiceReportBU"],
      [LuSettings, "Settings", "/settings", "mysettings"],
      [LuInfo, "Help", "/help", "help"],
    ],
  ],
];

const readSessionJSON = (key, fallback) => {
  const value = sessionStorage.getItem(key);
  if (!value || value === "undefined" || value === "null") return fallback;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn(`Ignoring invalid session value for ${key}`, error);
    return fallback;
  }
};

const createInvoiceCountRequest = (businessUserID) => ({
  _id: [],
  businessUserID,
  sortingType: "",
  sortingField: "",
  invoiceStatus: "All",
  activityStatus: "All",
  durationDate: "",
  fromDate: "",
  toDate: "",
  page: 1,
  limit: 1,
  registerView: true,
  filterValues: "",
  sendEmailFlag: false,
  searchParameterList: [
    "invoiceStatus",
    "activityStatus",
    "invoiceDate",
    "dueDate",
    "customerName",
    "invoiceNumber",
    "quotationNumber",
    "customerCompanyName",
    "purchaseOrderNumber",
  ],
});

const cacheInvoiceCount = (count) => {
  const stored = readSessionJSON("DASHBOARD_BADGES", {});
  sessionStorage.setItem(
    "DASHBOARD_BADGES",
    JSON.stringify({
      ...stored,
      invoiceBadge: count,
      invoiceBadgeSource: "invoice-register-unique",
    })
  );
};

const numberFrom = (value) =>
  Number(String(value ?? 0).replace(/[^0-9.-]/g, "")) || 0;
const money = (value, currency) =>
  `${currency || "$"}${formatCurrencyAUD(numberFrom(value))}`;
const firstNumber = (item) => {
  if (typeof item === "number") return item;
  if (!item) return 0;
  const key = Object.keys(item).find((name) =>
    /amount|total|count|value/i.test(name)
  );
  return numberFrom(key ? item[key] : 0);
};
const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};
const initials = (name) => {
  const parts = String(name || "IR")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "IR";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

function Brand() {
  return (
    <div className="dashboard-brand">
      <span>ir</span>
      <strong>invoice &amp; recover</strong>
    </div>
  );
}

export function DashboardNavigation({
  open = false,
  onClose = () => {},
  activePath = "",
  organisation,
  role,
  invoiceBadge,
  debtBadge,
  planName = "Plan",
  invoiceUsage,
  invoiceLimit,
  pageReady = true,
}) {
  const menuDispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;
  const [navController, navDispatch] = useMaterialUIController();
  const [localOpen, setLocalOpen] = useState(false);
  const [organisationOpen, setOrganisationOpen] = useState(false);
  const navUser = useMemo(
    () => readSessionJSON("LOGIN_DETAILS", {}),
    [location.key]
  );
  const organisationUserID = String(
    navUser._id || navUser.emailAddress || navUser.email || "anonymous"
  );
  const [switchingOrganisationID, setSwitchingOrganisationID] = useState(
    readOrganisationSwitchTarget
  );
  const switchingOrganisation = Boolean(switchingOrganisationID);
  const [createOrganisationOpen, setCreateOrganisationOpen] = useState(false);
  const [organisationContext, setOrganisationContext] = useState(() =>
    retainedOrganisationUserID === organisationUserID
      ? retainedOrganisationContext
      : null
  );
  const organisationSwitcherRef = useRef(null);
  const navigationLinksRef = useRef(null);
  const navigationScrollTopRef = useRef(readNavigationScrollTop());
  const isOpen = open || localOpen;
  const currentPath = activePath || pathname;
  const persistNavigationScroll = useCallback(() => {
    try {
      sessionStorage.setItem(
        NAVIGATION_SCROLL_STORAGE_KEY,
        String(navigationScrollTopRef.current)
      );
    } catch {}
  }, []);
  const closeNavigation = () => {
    persistNavigationScroll();
    setLocalOpen(false);
    onClose();
  };

  useLayoutEffect(() => {
    const navigationLinks = navigationLinksRef.current;
    if (navigationLinks) {
      navigationLinks.scrollTop = navigationScrollTopRef.current;
    }
    return persistNavigationScroll;
  }, [persistNavigationScroll]);

  const handleClose = () => {
    if (window.innerWidth > 1024) {
      setMiniSidenav(navDispatch, true);
      document.body.classList.add("sidebar-collapsed");
      try {
        sessionStorage.setItem("DESKTOP_SIDEBAR_COLLAPSED", "true");
      } catch {}
      window.dispatchEvent(
        new CustomEvent("desktop-sidebar-toggled", {
          detail: { collapsed: true },
        })
      );
    }
    closeNavigation();
  };

  useEffect(() => {
    const saved = sessionStorage.getItem("DESKTOP_SIDEBAR_COLLAPSED");
    if (saved === "true" && window.innerWidth > 1024) {
      setMiniSidenav(navDispatch, true);
      document.body.classList.add("sidebar-collapsed");
    }
  }, [navDispatch]);

  useEffect(() => {
    const openNavigation = () => {
      if (window.innerWidth > 1024) {
        setMiniSidenav(navDispatch, false);
        document.body.classList.remove("sidebar-collapsed");
        try {
          sessionStorage.setItem("DESKTOP_SIDEBAR_COLLAPSED", "false");
        } catch {}
        window.dispatchEvent(
          new CustomEvent("desktop-sidebar-toggled", {
            detail: { collapsed: false },
          })
        );
      } else {
        setLocalOpen(true);
      }
    };
    window.addEventListener("open-dashboard-navigation", openNavigation);
    return () =>
      window.removeEventListener("open-dashboard-navigation", openNavigation);
  }, [navDispatch]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") closeNavigation();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [isOpen]);

  useEffect(() => {
    if (!organisationOpen) return undefined;
    const closeSwitcher = (event) => {
      if (!organisationSwitcherRef.current?.contains(event.target)) {
        setOrganisationOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOrganisationOpen(false);
    };
    document.addEventListener("mousedown", closeSwitcher);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeSwitcher);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [organisationOpen]);

  const cachedData = useMemo(() => {
    try {
      const stored = readSessionJSON("DASHBOARD_BADGES", {});
      const planDetails = readSessionJSON("PLAN_DETAILS", []);
      const user = readSessionJSON("LOGIN_DETAILS", {});
      const roleObj = readSessionJSON("ROLE", {});
      const storedRoleName = sessionStorage.getItem("ROLE_NAME");
      const company =
        user.companyName ||
        user.businessName ||
        user.organisationName ||
        user.organizationName ||
        (Array.isArray(planDetails)
          ? planDetails[0]?.companyName
          : planDetails?.companyName) ||
        user.fullName ||
        user.name ||
        `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
        "My Organisation";
      const resolvedRole =
        (storedRoleName &&
        storedRoleName !== "null" &&
        storedRoleName !== "undefined"
          ? storedRoleName
          : null) ||
        roleObj.roleName ||
        user.roleName ||
        user.role ||
        (user.userType === "Business" ? "Owner" : user.userType) ||
        "Owner";
      return {
        ...stored,
        planName:
          stored.planName ||
          (Array.isArray(planDetails)
            ? planDetails[0]?.planName
            : planDetails?.planName) ||
          user.planName ||
          user.subscriptionPlan ||
          "SILVER",
        organisation: company,
        role: resolvedRole,
      };
    } catch {
      return {};
    }
  }, []);

  useEffect(() => {
    if (!switchingOrganisationID || !organisationContext || !pageReady)
      return undefined;
    if (
      String(organisationContext.activeBusinessUserID) !==
      String(switchingOrganisationID)
    )
      return undefined;

    let cancelled = false;
    const targetMembership = organisationContext.organisations?.find(
      (membership) =>
        String(membership.businessUserID) === String(switchingOrganisationID)
    );
    const logoFileName = targetMembership?.defaultLogoFileName;
    const waitForLogo = logoFileName
      ? new Promise((resolve) => {
          const image = new Image();
          const finish = () => resolve();
          image.onload = finish;
          image.onerror = finish;
          image.src = `${
            appConstants.baseURL
          }employee/viewFile?fileName=${encodeURIComponent(logoFileName)}`;
          if (image.complete) finish();
        })
      : Promise.resolve();
    const waitForFonts = document.fonts?.ready || Promise.resolve();

    Promise.all([waitForLogo, waitForFonts])
      .then(
        () =>
          new Promise((resolve) =>
            requestAnimationFrame(() => requestAnimationFrame(resolve))
          )
      )
      .then(() => {
        if (cancelled) return;
        try {
          sessionStorage.removeItem(ORGANISATION_SWITCH_STORAGE_KEY);
        } catch {}
        setSwitchingOrganisationID("");
      });

    return () => {
      cancelled = true;
    };
  }, [organisationContext, pageReady, switchingOrganisationID]);

  const cachedInvoiceCount =
    cachedData.invoiceBadgeSource === "invoice-register-unique"
      ? numberFrom(cachedData.invoiceBadge)
      : null;
  const [invoiceCount, setInvoiceCount] = useState(cachedInvoiceCount);
  const [debtCount, setDebtCount] = useState(cachedData.debtBadge ?? null);

  useEffect(() => {
    if (debtBadge == null) return;
    const count = numberFrom(debtBadge);
    setDebtCount(count);
    sessionStorage.setItem(
      "DASHBOARD_BADGES",
      JSON.stringify({
        ...readSessionJSON("DASHBOARD_BADGES", {}),
        debtBadge: count,
      })
    );
  }, [debtBadge]);

  useEffect(() => {
    // A null count means the current screen is already loading this total.
    if (invoiceBadge === null) return undefined;
    if (invoiceBadge !== undefined) {
      const count = numberFrom(invoiceBadge);
      setInvoiceCount(count);
      cacheInvoiceCount(count);
      setCachedRequest(
        `navigation-invoice-count:${navUser.businessUserID}`,
        count
      );
      return undefined;
    }

    if (!navUser.businessUserID) {
      setInvoiceCount(null);
      return undefined;
    }

    let cancelled = false;
    getCachedRequest(
      `navigation-invoice-count:${navUser.businessUserID}`,
      () =>
        axios
          .post(`${appConstants.baseURL}invoice/fetchInvoices`, {
            ...createInvoiceCountRequest(navUser.businessUserID),
            token: navUser.token,
          })
          .then((response) => {
            const output = response.data?.Result_Output || {};
            if (
              response.data?.Result_Status !== true ||
              output.totalDocs == null
            ) {
              throw new Error("Invoice navigation total is unavailable");
            }
            return numberFrom(output.totalDocs);
          }),
      15000
    )
      .then((count) => {
        if (cancelled) return;
        setInvoiceCount(count);
        cacheInvoiceCount(count);
      })
      .catch((error) => {
        if (!cancelled)
          console.error("Unable to load invoice navigation count", error);
      });

    return () => {
      cancelled = true;
    };
  }, [invoiceBadge, navUser.businessUserID, navUser.token, pathname]);

  const activePlanName =
    planName && planName !== "Plan"
      ? planName
      : cachedData.planName || "SILVER";
  const activeInvoiceUsage =
    invoiceUsage !== undefined ? invoiceUsage : cachedData.invoiceUsage ?? 8;
  const activeInvoiceLimit =
    invoiceLimit !== undefined ? invoiceLimit : cachedData.invoiceLimit ?? 10;
  const activeInvoiceBadge = invoiceCount ?? 0;
  const activeDebtBadge = debtCount ?? 0;
  const accountPermissions = organisationContext?.permissions || [];
  const organisations = organisationContext?.organisations || [];
  const activeBusinessUserID =
    organisationContext?.activeBusinessUserID || navUser.businessUserID;
  const activeMembership = organisations.find(
    (item) => String(item.businessUserID) === String(activeBusinessUserID)
  );
  // The authenticated membership is authoritative. Page-level profile props can
  // describe a customer, document owner, or stale dashboard response and must not
  // change the organisation switcher while navigating between routes.
  const activeOrganisation =
    activeMembership?.organisationName ||
    cachedData.organisation ||
    organisation ||
    "My Organisation";
  const activeRole =
    activeMembership?.roleName || cachedData.role || role || "Owner";
  const activeUserName =
    navUser.fullNameString?.fullName ||
    navUser.fullName ||
    `${navUser.firstName || ""} ${navUser.lastName || ""}`.trim() ||
    navUser.name ||
    navUser.emailAddress ||
    navUser.email ||
    "Signed-in user";
  const activeBusinessLogo = activeMembership?.defaultLogoFileName
    ? `${appConstants.baseURL}employee/viewFile?fileName=${encodeURIComponent(
        activeMembership.defaultLogoFileName
      )}`
    : "";
  const canCreateOrganisation = accountPermissions.includes(
    "organisation.create"
  );
  const canSwitchOrganisation =
    accountPermissions.includes("organisation.switch") &&
    organisations.length > 1;
  const canManageOrganisations = activeMembership?.roleKey === "OWNER";

  useEffect(() => {
    let active = true;
    if (retainedOrganisationUserID !== organisationUserID) {
      setOrganisationContext(null);
    }
    fetchOrganisationContext()
      .then((context) => {
        retainedOrganisationContext = context;
        retainedOrganisationUserID = organisationUserID;
        if (active) setOrganisationContext(context);
      })
      .catch(() => {
        if (active) {
          retainedOrganisationContext = null;
          retainedOrganisationUserID = organisationUserID;
          setOrganisationContext(null);
        }
      });
    return () => {
      active = false;
    };
  }, [organisationUserID]);

  useEffect(() => {
    const updateBusinessLogo = (event) => {
      const businessUserID = event.detail?.businessUserID;
      if (!businessUserID) return;
      setOrganisationContext((current) => {
        if (!current?.organisations) return current;
        const nextContext = {
          ...current,
          organisations: current.organisations.map((membership) =>
            String(membership.businessUserID) === String(businessUserID)
              ? {
                  ...membership,
                  defaultLogoFileName: event.detail?.defaultLogoFileName || "",
                }
              : membership
          ),
        };
        retainedOrganisationContext = nextContext;
        retainedOrganisationUserID = organisationUserID;
        return nextContext;
      });
    };
    window.addEventListener("inr:business-logo-changed", updateBusinessLogo);
    return () =>
      window.removeEventListener(
        "inr:business-logo-changed",
        updateBusinessLogo
      );
  }, []);

  const activateOrganisation = async (membership, switchFirst = true) => {
    if (switchingOrganisation) return;
    const targetBusinessUserID = String(membership.businessUserID || "");
    try {
      sessionStorage.setItem(
        ORGANISATION_SWITCH_STORAGE_KEY,
        targetBusinessUserID
      );
    } catch {}
    setOrganisationOpen(false);
    setSwitchingOrganisationID(targetBusinessUserID);
    try {
      const active = switchFirst
        ? await switchOrganisation(membership.businessUserID)
        : membership;
      const login = readSessionJSON("LOGIN_DETAILS", {});
      setAuthSessionItem(
        "LOGIN_DETAILS",
        JSON.stringify({
          ...login,
          businessUserID: active.businessUserID,
          companyName: active.organisationName,
          businessName: active.organisationName,
          roleID: active.roleID,
          roleName: active.roleName,
          accountPermissions: active.permissions || accountPermissions,
        })
      );
      setAuthSessionItem("ROLE_NAME", active.roleName);
      setAuthSessionItem(
        "ROLE",
        JSON.stringify({
          _id: active.roleID,
          roleName: active.roleName,
          applicableForBusinessUser: "Yes",
        })
      );
      const roleResponse = await axios.post(
        `${appConstants.baseURL}roles/fetchRoleRights`,
        { roleId: active.roleID }
      );
      if (roleResponse.data?.Result_Status)
        setAuthSessionItem(
          "ROLE_RIGHTS_LOGIN",
          JSON.stringify(roleResponse.data.Result_Output || [])
        );
      calculateRoleRights();
      const nextContext = {
        ...(organisationContext || {}),
        activeBusinessUserID: active.businessUserID,
      };
      retainedOrganisationContext = nextContext;
      retainedOrganisationUserID = organisationUserID;
      setOrganisationContext(nextContext);
      // Route definitions capture permission values when the application starts.
      // Reload once after an account switch so every route, screen and shared
      // module is rebuilt from the newly selected organisation's role rights.
      window.location.replace("/dashboard");
    } catch (error) {
      console.error("Unable to switch organisation", error);
      showDraftSavedMessage(
        error?.response?.data?.Result_Message ||
          error?.message ||
          "The organisation could not be switched. Try again.",
        "error",
        "Organisation switch failed"
      );
      try {
        sessionStorage.removeItem(ORGANISATION_SWITCH_STORAGE_KEY);
      } catch {}
      setSwitchingOrganisationID("");
    }
  };

  const renderNavItem = ([Icon, label, url, permissionKey, badge]) => {
    const permissionVal =
      screenRight[permissionKey] ??
      (permissionKey === "debts" ? screenRight.debtCollection : undefined);
    const locked = permissionVal === "None";
    const badgeCount =
      badge === "invoice"
        ? activeInvoiceBadge
        : badge === "debt"
        ? activeDebtBadge
        : 0;
    const content = (
      <>
        <Icon />
        <span className="dashboard-nav-label">{label}</span>
        {badgeCount > 0 ? (
          <span className="dashboard-nav-badge">{badgeCount}</span>
        ) : locked ? (
          <LuLock
            className="dashboard-lock-icon"
            aria-label="Requires plan upgrade"
          />
        ) : null}
      </>
    );
    return locked && !badgeCount ? (
      <button
        className="dashboard-locked-link"
        type="button"
        onClick={() => menuDispatch(menuAction(true))}
        key={label}
      >
        {content}
      </button>
    ) : (
      <Link
        className={matchesNavigationPath(currentPath, url) ? "active" : ""}
        aria-current={
          matchesNavigationPath(currentPath, url) ? "page" : undefined
        }
        to={url}
        key={label}
        onClick={closeNavigation}
      >
        {content}
      </Link>
    );
  };
  return (
    <>
      {switchingOrganisation && (
        <div
          className="dashboard-organisation-switch-overlay"
          role="status"
          aria-live="polite"
          aria-label="Switching organisation"
        >
          <aside aria-hidden="true">
            <div className="dashboard-switch-brand-skeleton">
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton width={118} height={13} radius={4} />
            </div>
            <div className="dashboard-switch-org-skeleton">
              <Skeleton variant="circular" width={32} height={32} />
              <div>
                <Skeleton width="82%" height={11} radius={4} />
                <Skeleton width="58%" height={9} radius={4} />
                <Skeleton width="40%" height={9} radius={4} />
              </div>
            </div>
            <div className="dashboard-switch-nav-skeleton">
              {Array.from({ length: 9 }, (_, index) => (
                <div key={index}>
                  <Skeleton
                    variant="rounded"
                    width={18}
                    height={18}
                    radius={4}
                  />
                  <Skeleton
                    width={`${52 + ((index * 11) % 32)}%`}
                    height={10}
                    radius={4}
                  />
                </div>
              ))}
            </div>
          </aside>
          <section aria-hidden="true">
            <header>
              <Skeleton width={320} height={40} radius={10} />
              <div>
                <Skeleton variant="pill" width={98} height={32} />
                <Skeleton variant="circular" width={32} height={32} />
              </div>
            </header>
            <main>
              <div className="dashboard-switch-heading-skeleton">
                <Skeleton width={210} height={30} radius={6} />
                <Skeleton width={360} height={12} radius={4} />
              </div>
              <div className="dashboard-switch-card-grid">
                {Array.from({ length: 3 }, (_, index) => (
                  <div key={index}>
                    <Skeleton width="42%" height={12} radius={4} />
                    <Skeleton width="68%" height={26} radius={5} />
                    <Skeleton width="54%" height={10} radius={4} />
                  </div>
                ))}
              </div>
              <div className="dashboard-switch-content-skeleton">
                <Skeleton width="24%" height={18} radius={5} />
                <Skeleton width="100%" height={44} radius={8} />
                {Array.from({ length: 6 }, (_, index) => (
                  <Skeleton key={index} width="100%" height={52} radius={6} />
                ))}
              </div>
            </main>
          </section>
          <span className="skeleton-sr-only">
            Switching organisation. Please wait.
          </span>
        </div>
      )}
      <button
        className="dashboard-sidebar-launcher"
        type="button"
        onClick={() => setLocalOpen(true)}
        aria-label="Open navigation"
      >
        <LuMenu />
      </button>
      <button
        className={`dashboard-overlay${isOpen ? " is-open" : ""}`}
        aria-label="Close navigation"
        onClick={closeNavigation}
      />
      <aside
        className={`dashboard-nav${isOpen ? " is-open" : ""}${
          navController.darkMode ? " dark" : ""
        }${pathname.startsWith("/debitNote") ? " debit-note-navigation" : ""}`}
      >
        <div className="dashboard-nav-brand">
          <Brand />
          <button
            type="button"
            className="dashboard-nav-close-btn"
            onClick={handleClose}
            aria-label="Close navigation"
            title="Close sidebar"
          >
            <LuPanelLeftClose size={18} />
          </button>
        </div>
        <div className="dashboard-org-wrap" ref={organisationSwitcherRef}>
          <button
            className={`dashboard-org${organisationOpen ? " is-open" : ""}${
              switchingOrganisation ? " is-switching" : ""
            }`}
            type="button"
            onClick={() => setOrganisationOpen((current) => !current)}
            aria-haspopup="menu"
            aria-expanded={organisationOpen}
            aria-label={
              switchingOrganisation ? "Switching organisation" : undefined
            }
            disabled={switchingOrganisation}
          >
            {switchingOrganisation ? (
              <div className="dashboard-org-switching" aria-hidden="true">
                <Skeleton
                  data-testid="organisation-switch-skeleton"
                  variant="circular"
                  width={32}
                  height={32}
                />
                <div>
                  <Skeleton
                    data-testid="organisation-switch-skeleton"
                    width="82%"
                    height={11}
                    radius={4}
                  />
                  <Skeleton
                    data-testid="organisation-switch-skeleton"
                    width="60%"
                    height={9}
                    radius={4}
                  />
                  <Skeleton
                    data-testid="organisation-switch-skeleton"
                    width="42%"
                    height={9}
                    radius={4}
                  />
                </div>
              </div>
            ) : (
              <>
                <span>
                  {initials(activeOrganisation)}
                  {activeBusinessLogo && (
                    <img
                      src={activeBusinessLogo}
                      alt={`${activeOrganisation} logo`}
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  )}
                </span>
                <div>
                  <strong>{activeOrganisation}</strong>
                  <small className="dashboard-org-user">{activeUserName}</small>
                  <small>{activeRole}</small>
                </div>
                <LuChevronsUpDown className="org-switch-icon" />
              </>
            )}
          </button>
          {organisationOpen && (
            <div className="dashboard-org-menu" role="menu">
              {(organisations.length
                ? organisations
                : [
                    {
                      businessUserID: navUser.businessUserID,
                      organisationName: activeOrganisation,
                      roleName: activeRole,
                    },
                  ]
              ).map((item) => {
                const isActive =
                  String(item.businessUserID) ===
                  String(
                    organisationContext?.activeBusinessUserID ||
                      navUser.businessUserID
                  );
                if (!isActive && !canSwitchOrganisation) return null;
                return (
                  <button
                    className={isActive ? "active" : ""}
                    type="button"
                    role="menuitem"
                    key={item.businessUserID || item.organisationName}
                    disabled={isActive}
                    onClick={() => activateOrganisation(item)}
                  >
                    <LuBuilding2 />
                    <span>
                      {item.organisationName} — {item.roleName}
                    </span>
                    {isActive && (
                      <LuCheck className="dashboard-org-menu-check" />
                    )}
                  </button>
                );
              })}
              {canCreateOrganisation && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOrganisationOpen(false);
                    setCreateOrganisationOpen(true);
                  }}
                >
                  <LuPlus />
                  <span>Create organisation</span>
                </button>
              )}
              {canManageOrganisations && (
                <Link
                  role="menuitem"
                  to="/settings?tab=business"
                  onClick={() => {
                    setOrganisationOpen(false);
                    closeNavigation();
                  }}
                >
                  <LuSettings />
                  <span>Manage organisations</span>
                </Link>
              )}
            </div>
          )}
        </div>
        <nav
          className="dashboard-nav-links"
          aria-label="Primary navigation"
          ref={navigationLinksRef}
          onScroll={(event) => {
            navigationScrollTopRef.current = event.currentTarget.scrollTop;
          }}
        >
          <Link
            className={
              currentPath === "/dashboard" || currentPath === "/"
                ? "active"
                : ""
            }
            to="/dashboard"
            onClick={closeNavigation}
          >
            <LuLayoutDashboard />
            <span className="dashboard-nav-label">Dashboard</span>
          </Link>
          {NAV_GROUPS.map(([heading, links]) => (
            <div className="dashboard-nav-group" key={heading}>
              <p>{heading}</p>
              {links.map(renderNavItem)}
            </div>
          ))}
        </nav>
        <div className="dashboard-plan">
          <div className="dashboard-plan-header">
            <strong>{String(activePlanName || "SILVER").toUpperCase()}</strong>
            <Link to="/subscriptions" onClick={closeNavigation}>
              Upgrade
            </Link>
          </div>
          <div className="dashboard-plan-usage">
            <span>Invoices</span>
            <b>
              {activeInvoiceUsage} / {activeInvoiceLimit || "—"}
            </b>
          </div>
          <div className="dashboard-plan-progress">
            <span
              style={{
                width: activeInvoiceLimit
                  ? `${Math.min(
                      100,
                      Math.max(
                        0,
                        (activeInvoiceUsage / activeInvoiceLimit) * 100
                      )
                    )}%`
                  : "0%",
              }}
            />
          </div>
        </div>
      </aside>
      <OrganisationCreateDialog
        open={createOrganisationOpen}
        onClose={() => setCreateOrganisationOpen(false)}
        onCreated={(membership) => activateOrganisation(membership, false)}
      />
    </>
  );
}

export function ProfileMenu({
  dark,
  controllerDispatch,
  onClose,
  userName,
  credits,
  navigate,
}) {
  return (
    <div className="dashboard-profile-menu">
      <div className="dashboard-credits">
        <LuCoins />
        <span>Credits</span>
        <strong>{credits}</strong>
      </div>
      <button
        className="dashboard-profile-help"
        onClick={() => navigate("/help")}
      >
        <LuCircleHelp />
        Help
      </button>
      <button onClick={() => navigate("/settings?tab=profile")}>
        <LuUser />
        View profile
      </button>
      <button onClick={() => navigate("/settings")}>
        <LuSettings />
        Account settings
      </button>
      <div className="dashboard-appearance">
        <span>Appearance</span>
        <div>
          <button
            type="button"
            className={!dark ? "active" : ""}
            aria-pressed={!dark}
            onClick={() => setDarkMode(controllerDispatch, false)}
          >
            <LuSun />
            Light
          </button>
          <button
            type="button"
            className={dark ? "active" : ""}
            aria-pressed={dark}
            onClick={() => setDarkMode(controllerDispatch, true)}
          >
            <LuMoon />
            Dark
          </button>
        </div>
      </div>
      <button
        onClick={() => {
          clearAuthSession();
          window.location.replace("/login");
        }}
      >
        <LuLogOut />
        Sign out
      </button>
      {/* <small>Signed in as {userName}</small> */}
      <button
        className="dashboard-profile-close"
        onClick={onClose}
        aria-label="Close menu"
      >
        <LuX />
      </button>
    </div>
  );
}

function MetricCard({
  icon: Icon,
  title,
  value,
  subtitle,
  rows,
  note,
  positive,
}) {
  return (
    <article className="dashboard-card dashboard-metric">
      <header>
        <h2>{title}</h2>
        <Icon />
      </header>
      <strong className="dashboard-money">{value}</strong>
      <p>{subtitle}</p>
      <div className="dashboard-metric-rows">
        {rows.map(([label, amount]) => (
          <div key={label}>
            <span>{label}</span>
            <b>{amount}</b>
          </div>
        ))}
      </div>
      {positive && <div className="dashboard-positive">{positive}</div>}
      {note && (
        <div className="dashboard-note">
          <LuCircleHelp />
          {note}
        </div>
      )}
    </article>
  );
}

function ReceivablesChart({ values }) {
  const chartValues = Array.from(
    { length: 6 },
    (_, index) => values[index] || 0
  );
  const labels = [
    "31+ days late",
    "1–30 days late",
    "Today",
    "+1–7 days",
    "8–30 days",
    "+31 days",
  ];
  const max = Math.max(...chartValues, 1);
  const scaleMax = Math.ceil(max / 3000) * 3000 || 3000;
  const scaleValues = [scaleMax, (scaleMax * 2) / 3, scaleMax / 3, 0].map(
    (value) => `$${Math.round(value / 1000)}k`
  );
  const overdueTotal = chartValues[0] + chartValues[1];
  const dueTotal = chartValues[2];
  const futureTotal = chartValues
    .slice(3)
    .reduce((sum, value) => sum + value, 0);

  return (
    <div className="dashboard-chart-container">
      <div className="dashboard-plot-row">
        <div className="dashboard-currency-scale">
          {scaleValues.map((s) => (
            <span key={s}>{s}</span>
          ))}
        </div>
        <div className="dashboard-bar-chart">
          {chartValues.map((value, index) => (
            <div className="dashboard-bar-item" key={labels[index]}>
              <div className="dashboard-bar-track">
                <b>{(value / 1000).toFixed(value % 1000 ? 1 : 0)}</b>
                <i
                  className={
                    index < 2 ? "overdue" : index === 2 ? "due" : "future"
                  }
                  style={{
                    height: `${
                      value ? Math.max(4, (value / scaleMax) * 184) : 0
                    }px`,
                  }}
                />
              </div>
              <span className="dashboard-bar-label">{labels[index]}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="dashboard-legend">
        <div className="dashboard-legend-item">
          <span className="legend-marker overdue" />
          <span>Overdue</span>
        </div>
        <div className="dashboard-legend-item">
          <span className="legend-marker due" />
          <span>Due</span>
        </div>
        <div className="dashboard-legend-item">
          <span className="legend-marker future" />
          <span>Future</span>
        </div>
      </div>
      <p className="dashboard-chart-summary">
        Overdue {money(overdueTotal)} · Due {money(dueTotal)} · Future{" "}
        {money(futureTotal)} · Total{" "}
        {money(overdueTotal + dueTotal + futureTotal)}
      </p>
    </div>
  );
}

function CashChart({ incoming, outgoing }) {
  const ins = Array.from(
    { length: 6 },
    (_, index) => incoming.slice(-6)[index] || 0
  );
  const outs = Array.from(
    { length: 6 },
    (_, index) => outgoing.slice(-6)[index] || 0
  );
  const max = Math.max(...ins, ...outs, 1);
  const scaleMax = Math.ceil(max / 3000) * 3000 || 3000;
  const months = Array.from({ length: 6 }, (_, index) =>
    new Date(
      new Date().getFullYear(),
      new Date().getMonth() - 5 + index,
      1
    ).toLocaleDateString("en-AU", { month: "short" })
  );
  const scaleValues = [scaleMax, (scaleMax * 2) / 3, scaleMax / 3, 0].map(
    (value) => `$${Math.round(value / 1000)}k`
  );
  const totalIn = ins.reduce((a, b) => a + b, 0);
  const totalOut = outs.reduce((a, b) => a + b, 0);
  const net = totalIn - totalOut;

  return (
    <div className="dashboard-chart-container">
      <div className="dashboard-plot-row">
        <div className="dashboard-currency-scale">
          {scaleValues.map((s) => (
            <span key={s}>{s}</span>
          ))}
        </div>
        <div className="dashboard-cash-chart">
          {months.map((month, index) => (
            <div className="dashboard-cash-item" key={month}>
              <div className="dashboard-cash-track">
                <div className="cash-col">
                  <b>{Math.round(ins[index] / 1000)}</b>
                  <i
                    className="in"
                    style={{
                      height: `${
                        ins[index]
                          ? Math.max(4, (ins[index] / scaleMax) * 184)
                          : 0
                      }px`,
                    }}
                  />
                </div>
                <div className="cash-col">
                  <b>{Math.round(outs[index] / 1000)}</b>
                  <i
                    className="out"
                    style={{
                      height: `${
                        outs[index]
                          ? Math.max(4, (outs[index] / scaleMax) * 184)
                          : 0
                      }px`,
                    }}
                  />
                </div>
              </div>
              <span className="dashboard-bar-label">{month}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="dashboard-legend">
        <div className="dashboard-legend-item">
          <span className="legend-marker future" />
          <span>Cash in</span>
        </div>
        <div className="dashboard-legend-item">
          <span className="legend-marker cash-out" />
          <span>Cash out</span>
        </div>
      </div>
      <p className="dashboard-chart-summary">
        In ${totalIn.toLocaleString()} · Out ${totalOut.toLocaleString()} · Net
        ${net.toLocaleString()}
      </p>
    </div>
  );
}

function RecentInvoices({ rows, currency, loading }) {
  const navigate = useNavigate();
  const displayRows = Array.isArray(rows) ? rows : [];
  const openInvoice = (row) => {
    if (row?._id) navigate(`/invoices/view/${row._id}`);
  };
  const handleRowKeyDown = (event, row) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openInvoice(row);
    }
  };

  return (
    <section className="dashboard-card dashboard-invoices">
      <header>
        <div>
          <h2>Recent invoices</h2>
        </div>
        <Link to="/invoices">View all invoices</Link>
      </header>
      <div className="dashboard-table">
        <div className="dashboard-table-head">
          <span>Invoice number</span>
          <span>Customer</span>
          <span>Invoice date</span>
          <span>Due date</span>
          <span>Invoice status</span>
          <span>Payment status</span>
          <span>Invoice total</span>
          <span>Outstanding balance</span>
        </div>
        {loading &&
          Array.from({ length: 5 }, (_, index) => (
            <div
              className="dashboard-table-row dashboard-table-row--loading"
              key={`dashboard-invoice-loading-${index}`}
            >
              <Skeleton width="68%" height={14} />
              <Skeleton width="74%" height={14} />
              <Skeleton width="64%" height={14} />
              <Skeleton width="68%" height={14} />
              <Skeleton width="54%" height={22} radius={999} />
              <Skeleton width="54%" height={22} radius={999} />
              <Skeleton width="64%" height={14} />
              <Skeleton width="68%" height={14} />
            </div>
          ))}
        {!loading && displayRows.length === 0 && (
          <div className="dashboard-empty-state">No recent invoices found.</div>
        )}
        {!loading &&
          displayRows.map((row, index) => {
            const invoice =
              row.invoiceNumber ||
              row.invoiceNo ||
              row.number ||
              `INV-${index + 1}`;
            const customer =
              row.customerCompanyName ||
              row.customerName ||
              row.companyName ||
              row.contactName ||
              "—";
            const rawPaymentStatus = String(
              row.invoiceStatus || row.paymentStatusLabel || ""
            )
              .trim()
              .toLowerCase();
            const rawActivityStatus = String(
              row.activityStatus || row.status || ""
            )
              .trim()
              .toLowerCase();
            const dueDate = new Date(row.dueDateOriginal || row.dueDate || 0);
            const isOverdue =
              !Number.isNaN(dueDate.getTime()) && dueDate < new Date();
            const isDraft =
              !(Number(row.emailSent) > 0) &&
              (!rawActivityStatus ||
                rawActivityStatus === "draft" ||
                rawActivityStatus === "pending");
            const paymentKey = rawPaymentStatus.includes("void")
              ? "voided"
              : rawPaymentStatus === "paid" || rawPaymentStatus === "fully paid"
              ? "paid"
              : rawPaymentStatus.includes("partial")
              ? "awaiting"
              : isDraft
              ? "draft"
              : isOverdue
              ? "overdue"
              : "awaiting";
            const activityStatus =
              String(row.activityStatus || "").trim() ||
              (Number(row.emailSent) > 0 ? "Sent" : "Draft");
            const paymentStatus =
              paymentKey === "draft"
                ? "Not issued"
                : String(
                    row.invoiceStatus || row.paymentStatusLabel || ""
                  ).trim() ||
                  ({
                    overdue: "Overdue",
                    awaiting: "Awaiting payment",
                    paid: "Paid",
                    voided: "Voided",
                  }[paymentKey] ??
                    "Awaiting payment");
            const amount =
              row.finalAmount ?? row.totalAmount ?? row.invoiceAmount ?? 0;
            const balance =
              row.payablePendingAmount !== undefined
                ? row.payablePendingAmount
                : row.paymentStatus;
            const due = row.dueDate || row.paymentDueDate;
            const invoiceDate = row.invoiceDate || row.createdDate;
            return (
              <article
                className="dashboard-table-row dashboard-invoice-row"
                key={row._id || invoice}
                onClick={() => openInvoice(row)}
                onKeyDown={(event) => handleRowKeyDown(event, row)}
                tabIndex={row._id ? 0 : undefined}
                role={row._id ? "link" : undefined}
                aria-label={row._id ? `View invoice ${invoice}` : undefined}
              >
                <span className="invoice-number">{invoice}</span>
                <span className="customer">
                  {customer}
                  {row.quotationNumber && (
                    <small>Reference: {row.quotationNumber}</small>
                  )}
                </span>
                <span className="invoice-date">{formatDate(invoiceDate)}</span>
                <span className="due-date">
                  {paymentKey === "draft" && "Planned "}
                  {due ? formatDate(due) : "—"}
                </span>
                <span className="invoice-lifecycle-status">
                  <i
                    className={String(activityStatus)
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")}
                  >
                    {activityStatus}
                  </i>
                </span>
                <span className="invoice-payment-status">
                  <i className={paymentKey}>{paymentStatus}</i>
                  {paymentKey === "overdue" && <small>Payment overdue</small>}
                </span>
                <strong className="invoice-amount">
                  {money(amount, currency)}
                </strong>
                <span className="invoice-balance">
                  <small>Outstanding balance</small>
                  <strong>
                    {paymentKey === "draft"
                      ? "— Not issued"
                      : money(balance, currency)}
                  </strong>
                </span>
              </article>
            );
          })}
      </div>
      <footer>
        {displayRows.length} recent records · Paid invoices are a subset of this
        month&apos;s collections · Drafts are not receivables
        <br />
        Status includes payment and delivery stages; “Sent” confirms delivery,
        not payment.
      </footer>
    </section>
  );
}

function MetricSkeleton() {
  return (
    <article className="dashboard-card dashboard-metric dashboard-data-skeleton">
      <header>
        <Skeleton width="42%" height={18} />
        <Skeleton width={20} height={20} radius={6} />
      </header>
      <Skeleton width="58%" height={30} radius={8} />
      <Skeleton width="72%" height={13} />
      <div className="dashboard-metric-rows">
        <div>
          <Skeleton width="36%" height={13} />
          <Skeleton width="42%" height={13} />
        </div>
        <div>
          <Skeleton width="32%" height={13} />
          <Skeleton width="48%" height={13} />
        </div>
      </div>
      <Skeleton width="100%" height={42} radius={10} />
    </article>
  );
}

function AttentionSkeleton() {
  return (
    <div className="dashboard-attention-row dashboard-data-skeleton">
      <div className="dashboard-attention-context">
        <Skeleton width={20} height={20} radius={6} />
        <div>
          <Skeleton width="min(62vw, 360px)" height={15} />
          <Skeleton width="min(54vw, 300px)" height={13} />
        </div>
      </div>
      <Skeleton width={76} height={24} radius={999} />
      <Skeleton width={126} height={36} radius={10} />
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div
      className="dashboard-chart-container dashboard-chart-skeleton"
      aria-hidden="true"
    >
      <div className="dashboard-chart-skeleton-bars">
        {[42, 70, 54, 82, 62, 76].map((height, index) => (
          <Skeleton
            key={index}
            width="11%"
            height={`${height}%`}
            radius="6px 6px 0 0"
          />
        ))}
      </div>
      <Skeleton width="68%" height={13} />
    </div>
  );
}

export default function BusinessUserDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const currency = useSelector((state) => state.currency.currencySymbol);
  const location = useLocation();
  const user = useMemo(
    () => readSessionJSON("LOGIN_DETAILS", {}),
    [location.key]
  );
  const roleName =
    sessionStorage.getItem("ROLE_NAME") ||
    JSON.parse(sessionStorage.getItem("ROLE") || "{}")?.roleName;
  const isSuperAdmin =
    roleName === "Super Admin" || user?.userType?.toLowerCase() === "admin";

  useEffect(() => {
    if (isSuperAdmin) {
      navigate("/dashboard-su", { replace: true });
    }
  }, [isSuperAdmin, navigate]);
  const plan = useMemo(() => {
    const details = readSessionJSON("PLAN_DETAILS", []);
    return Array.isArray(details) ? details[0] || {} : details || {};
  }, []);
  const organisation =
    user.companyName ||
    user.businessName ||
    user.organisationName ||
    user.organizationName ||
    plan?.companyName ||
    user.fullName ||
    user.name ||
    `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
    "My Organisation";
  const role =
    (roleName && roleName !== "null" && roleName !== "undefined"
      ? roleName
      : null) ||
    user.roleName ||
    user.role ||
    (user.userType === "Business" ? "Owner" : user.userType) ||
    "Owner";
  const userName =
    user.fullName ||
    `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
    user.name ||
    organisation ||
    user.emailAddress ||
    user.email;
  const profileFileName =
    user.profilePictureFileName || user.profilePicFileName;
  const rawProfileImage = user.profileImage || user.avatar || user.photo;
  const validFileName =
    profileFileName &&
    profileFileName !== "null" &&
    profileFileName !== "undefined" &&
    profileFileName !== "empty"
      ? profileFileName
      : null;
  const profileImage =
    rawProfileImage &&
    typeof rawProfileImage === "string" &&
    (rawProfileImage.startsWith("http://") ||
      rawProfileImage.startsWith("https://") ||
      rawProfileImage.startsWith("data:") ||
      rawProfileImage.startsWith("blob:"))
      ? rawProfileImage
      : validFileName
      ? `${appConstants.baseURL}user/viewFile?fileName=${encodeURIComponent(
          validFileName
        )}`
      : "";
  const initialCredits = useMemo(() => {
    const storedCredits = readSessionJSON("CREDITS", null);
    return Number(storedCredits ?? user.availableCredits ?? 0);
  }, [user.availableCredits]);
  const [creditBalance, setCreditBalance] = useState(initialCredits);
  const credits = creditBalance.toLocaleString("en-AU");
  const [navOpen, setNavOpen] = useState(false);
  const [createInvoiceModalOpen, setCreateInvoiceModalOpen] = useState(false);
  const [period, setPeriod] = useState("30days");
  const [dashboard, setDashboard] = useState({});
  const [receivables, setReceivables] = useState([]);
  const [cashIn, setCashIn] = useState([]);
  const [cashOut, setCashOut] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [attentionInvoices, setAttentionInvoices] = useState({
    draftCount: 0,
    draftAmount: 0,
    overdueCount: 0,
    overdueAmount: 0,
  });
  const [viewedQuoteCount, setViewedQuoteCount] = useState(0);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [loadedBusinessUserID, setLoadedBusinessUserID] = useState("");
  const params = useMemo(() => {
    const days = period === "7days" ? 7 : period === "quarter" ? 90 : 30;
    const customTo = new Date();
    const customFrom = new Date(customTo);
    customFrom.setDate(customFrom.getDate() - days);
    return {
      businessUserID: user.businessUserID,
      dashboardDuration: "custom",
      customFrom: customFrom.toISOString(),
      customTo: customTo.toISOString(),
      page: 1,
      limit: 5,
      sortingType: "desc",
      sortingField: "createdAt",
    };
  }, [period, user.businessUserID]);

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    await Promise.all([
      getBusinessUserDashboardCardData(
        params,
        (data) =>
          setDashboard(data?.output?.[0] || data?.Result_Output?.[0] || {}),
        dispatch
      ),
      getBUDashboardInvoicesOwedToYouGraphDetails(
        params,
        (data) => setReceivables((data?.Result_Output || []).map(firstNumber)),
        dispatch
      ),
      getBusinessUserDasboardLineChartDataOrderRevenue(
        params,
        (data) => {
          setCashIn((data?.output?.Invoice_revenue || []).map(firstNumber));
          setCashOut((data?.output?.RCTI_revenue || []).map(firstNumber));
        },
        dispatch
      ),
      axios
        .post(`${appConstants.baseURL}invoice/fetchInvoices`, {
          ...createInvoiceCountRequest(user.businessUserID),
          page: 1,
          limit: 1000,
          sortingType: -1,
          sortingField: "createdDate",
          token: user.token,
        })
        .then(({ data }) => {
          const docs = data?.Result_Output?.docs || [];
          const classifiedInvoices = docs.map((invoice) => ({
            invoice,
            status: getInvoiceStatusKey(invoice),
          }));
          const drafts = classifiedInvoices.filter(
            ({ status }) => status === "draft"
          );
          const overdue = classifiedInvoices.filter(
            ({ status }) => status === "overdue"
          );
          setAttentionInvoices({
            draftCount: drafts.length,
            draftAmount: drafts.reduce(
              (sum, { invoice }) => sum + numberFrom(invoice.finalAmount),
              0
            ),
            overdueCount: overdue.length,
            overdueAmount: overdue.reduce(
              (sum, { invoice }) =>
                sum +
                numberFrom(invoice.payablePendingAmount ?? invoice.finalAmount),
              0
            ),
          });
          const newest = [...docs].sort(
            (a, b) =>
              new Date(b.createdDate || b.createdAt || b.invoiceDate || 0) -
              new Date(a.createdDate || a.createdAt || a.invoiceDate || 0)
          );
          setInvoices(newest.slice(0, 5));
        })
        .catch(() => {
          setInvoices([]);
          setAttentionInvoices({
            draftCount: 0,
            draftAmount: 0,
            overdueCount: 0,
            overdueAmount: 0,
          });
        }),
      axios
        .post(`${appConstants.baseURL}quotation/fetchQuotations`, {
          businessUserID: user.businessUserID,
          sortingType: "",
          sortingField: "",
          quotationStatus: "All",
          activityStatus: "Viewed",
          customer: "All customers",
          durationDate: "",
          fromDate: "",
          toDate: "",
          page: 1,
          limit: 1,
          filterValues: "",
          searchParameterList: [
            "quotationNumber",
            "activityStatus",
            "quotationStatus",
            "quotationDate",
            "customerName",
            "finalAmount",
            "customerCompanyName",
          ],
          token: user.token,
        })
        .then(({ data }) => {
          const output = data?.Result_Output || {};
          setViewedQuoteCount(
            numberFrom(output.totalDocs ?? output.docs?.length ?? 0)
          );
        })
        .catch(() => setViewedQuoteCount(0)),
    ]);
    setLoadedBusinessUserID(String(params.businessUserID || ""));
    setDashboardLoading(false);
  }, [params, dispatch]);
  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);
  useEffect(() => {
    if (!user.businessUserID) return;
    getCachedRequest(
      `credits:${user.businessUserID}`,
      () =>
        axios.post(`${appConstants.baseURL}login/fetchCredit`, {
          businessUserID: user.businessUserID,
        }),
      30000
    )
      .then((response) => {
        const balance = Number(
          response.data?.Result_Output?.[0]?.availableCredits ?? 0
        );
        setCreditBalance(balance);
        sessionStorage.setItem("CREDITS", JSON.stringify(balance));
      })
      .catch(() => undefined);
  }, [user.businessUserID]);

  const invoiceData = dashboard.invoicesCount?.[0] || {};
  const rctiData = dashboard.rctiCount?.[0] || {};
  const invoiceRows = invoices;
  const unpaid = invoiceData.TotalUnpaidAmount || 0;
  const overdue = invoiceData.TotalOverduePaymentAmount || 0;
  const awaiting =
    invoiceData.TotalAwaitingPaymentAmount || Math.max(0, unpaid - overdue);
  const collected = numberFrom(invoiceData.TotalPaidAmount);
  const previousCollected = numberFrom(
    dashboard.previousCollectedAmount ?? invoiceData.PreviousCollectedAmount
  );
  const averageDaysToPay = numberFrom(
    dashboard.averageDaysToPay ?? invoiceData.AverageDaysToPay
  );
  const collectedChange = previousCollected
    ? ((collected - previousCollected) / previousCollected) * 100
    : null;
  const planName =
    plan.planName ||
    user.planName ||
    user.subscriptionPlan ||
    user.subscriptionPlanName ||
    "SILVER";
  const invoiceUsage = numberFrom(invoiceData.TotalInvoiceCount);
  const invoiceLimit = numberFrom(
    plan.invoiceLimit ??
      plan.invoiceFeatureCount ??
      plan.featureCount ??
      user.invoiceLimit ??
      user.invoiceFeatureCount ??
      user.featureCount
  );

  useEffect(() => {
    if (invoiceData.TotalInvoiceCount !== undefined || plan.planName) {
      sessionStorage.setItem(
        "DASHBOARD_BADGES",
        JSON.stringify({
          ...readSessionJSON("DASHBOARD_BADGES", {}),
          ...(invoiceData.TotalDebtCount != null
            ? { debtBadge: numberFrom(invoiceData.TotalDebtCount) }
            : {}),
          invoiceUsage: invoiceUsage || 8,
          invoiceLimit: invoiceLimit || 10,
          planName: planName || "SILVER",
        })
      );
    }
  }, [invoiceData, plan, user, invoiceUsage, invoiceLimit, planName]);

  const periodLabels = {
    "7days": "7 days",
    "30days": "30 days",
    quarter: "Quarter",
  };
  const now = new Date();
  const periodStart = new Date(now);
  periodStart.setDate(
    now.getDate() - (period === "7days" ? 7 : period === "quarter" ? 90 : 30)
  );
  const formatPeriodRange = (start, end) => {
    if (
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear()
    ) {
      return `${start.getDate()}–${end.getDate()} ${end.toLocaleDateString(
        "en-AU",
        { month: "short", year: "numeric" }
      )}`;
    }
    return `${formatDate(start)} – ${formatDate(end)}`;
  };
  const periodText = `Collections period · ${formatPeriodRange(
    periodStart,
    now
  )}`;
  const updatedText = `Last updated ${formatDate(
    now
  )} at ${now.toLocaleTimeString("en-AU", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;

  return (
    <div
      className={`business-dashboard${controller.darkMode ? " dark" : ""}`}
      data-theme={controller.darkMode ? "dark" : "light"}
    >
      <DashboardNavigation
        open={navOpen}
        onClose={() => setNavOpen(false)}
        organisation={organisation}
        role={role}
        debtBadge={
          invoiceData.TotalDebtCount != null
            ? numberFrom(invoiceData.TotalDebtCount)
            : undefined
        }
        planName={planName || "SILVER"}
        invoiceUsage={invoiceUsage || 8}
        invoiceLimit={invoiceLimit || 10}
        pageReady={
          !dashboardLoading &&
          loadedBusinessUserID === String(user.businessUserID || "")
        }
      />
      <div className="dashboard-main">
        {/* FIXED UNTOUCHABLE APP HEADER (Figma Node 429:22151) */}
        <AppHeader
          onOpenNav={() => {
            if (window.innerWidth <= 1024) {
              setNavOpen(true);
            }
          }}
          userName={userName}
          credits={credits}
          profileImage={profileImage}
        />
        <main className="dashboard-content">
          <section className="dashboard-mobile-context">
            <div className="dashboard-org-product-row">
              <div className="dashboard-product-identity">
                <button
                  type="button"
                  className="dashboard-launcher-btn"
                  onClick={() => setNavOpen(true)}
                  aria-label="Open navigation"
                >
                  <LuMenu />
                </button>
                <span className="dashboard-product-name">
                  invoice &amp; recover
                </span>
              </div>
              <button className="dashboard-mobile-org" type="button">
                <span>{initials(organisation)}</span>
                <div>
                  <strong>{organisation}</strong>
                  <small className="dashboard-org-user">{userName}</small>
                  <small>{role}</small>
                </div>
                <LuChevronsUpDown className="org-switch-icon" />
              </button>
            </div>
            <div className="dashboard-shortcuts">
              <Link to="/invoiceReport" className="dashboard-shortcut">
                Reports
              </Link>
              <Link to="/invoices" className="dashboard-shortcut">
                Invoices {invoiceData.TotalOverduePaymentCount || 6}
              </Link>
              <Link to="/debts" className="dashboard-shortcut">
                Debt collection {invoiceData.TotalDebtCount || 6}
              </Link>
            </div>
          </section>
          <section className="dashboard-heading">
            <div>
              <h1>Dashboard</h1>
              <p>{organisation} · All amounts in AUD</p>
            </div>
            <div className="dashboard-create">
              <Link to="/quotations/add?type=new">
                <LuPlus />
                New quote
              </Link>
              <button
                type="button"
                className="primary"
                onClick={() => setCreateInvoiceModalOpen(true)}
              >
                <LuPlus />
                New invoice
              </button>
            </div>
            <div className="dashboard-period-info">
              <b>{periodText}</b>
              <span>{updatedText}</span>
            </div>
            <div
              className="dashboard-periods"
              role="tablist"
              aria-label="Collections period selector"
            >
              {Object.entries(periodLabels).map(([value, label]) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={period === value}
                  className={period === value ? "active" : ""}
                  onClick={() => setPeriod(value)}
                  key={value}
                >
                  {label}
                </button>
              ))}
            </div>
          </section>
          <section className="dashboard-metrics">
            {dashboardLoading ? (
              <>
                <MetricSkeleton />
                <MetricSkeleton />
                <MetricSkeleton />
              </>
            ) : (
              <>
                <MetricCard
                  icon={LuFileText}
                  title="To receive"
                  value={money(unpaid, currency)}
                  subtitle="Current issued unpaid balance"
                  rows={[
                    [
                      "Awaiting",
                      `${invoiceData.TotalAwaitingPaymentCount || 0} · ${money(
                        awaiting,
                        currency
                      )}`,
                    ],
                    [
                      "Overdue",
                      `${invoiceData.TotalOverduePaymentCount || 0} · ${money(
                        overdue,
                        currency
                      )}`,
                    ],
                  ]}
                  note={`Overdue is included in the ${money(
                    unpaid,
                    currency
                  )} balance. Unsent drafts excluded.`}
                />
                <MetricCard
                  icon={LuLink}
                  title="To be paid"
                  value={money(rctiData.TotalRctiUnpaidAmount || 0, currency)}
                  subtitle="Current outstanding outgoing"
                  rows={[
                    [
                      "RCTIs awaiting",
                      `${rctiData.TotalAwaitingRCTIPaymentCount || 0} · ${money(
                        rctiData.TotalAwaitingRCTIPaymentAmount || 0,
                        currency
                      )}`,
                    ],
                    [
                      "Expenses due",
                      `${rctiData.TotalOverdueRCTIPaymentCount || 0} · ${money(
                        rctiData.TotalOverdueRCTIPaymentAmount || 0,
                        currency
                      )}`,
                    ],
                  ]}
                  note="Outstanding now, not period spending."
                />
                <MetricCard
                  icon={LuCircleCheck}
                  title="Collected"
                  value={money(collected, currency)}
                  subtitle={`Actual payments · ${formatPeriodRange(
                    periodStart,
                    now
                  )}`}
                  rows={[
                    [
                      "vs previous period",
                      collectedChange === null
                        ? "—"
                        : `${
                            collectedChange >= 0 ? "+" : ""
                          }${collectedChange.toFixed(1)}% vs ${money(
                            previousCollected,
                            currency
                          )}`,
                    ],
                    [
                      "Previous period",
                      previousCollected
                        ? money(previousCollected, currency)
                        : "—",
                    ],
                    [
                      "Average time to pay",
                      averageDaysToPay ? `${averageDaysToPay} days` : "—",
                    ],
                  ]}
                />
              </>
            )}
          </section>
          <section className="dashboard-card dashboard-attention">
            <header>
              <h2>Needs attention</h2>
              <p>Recommended next steps</p>
            </header>
            {dashboardLoading ? (
              <>
                <AttentionSkeleton />
                <AttentionSkeleton />
                <AttentionSkeleton />
              </>
            ) : (
              <>
                <div className="dashboard-attention-row">
                  <div className="dashboard-attention-context">
                    <LuClock />
                    <div>
                      <strong>
                        {attentionInvoices.overdueCount} overdue invoices ·{" "}
                        {money(attentionInvoices.overdueAmount, currency)}
                      </strong>
                      <p>
                        Review overdue invoices and follow up with customers
                      </p>
                    </div>
                  </div>
                  <em className="status-pill status-overdue">Overdue</em>
                  <Link className="primary" to="/invoices?tab=overdue">
                    Send reminders
                  </Link>
                </div>
                <div className="dashboard-attention-row">
                  <div className="dashboard-attention-context">
                    <LuEye />
                    <div>
                      <strong>{viewedQuoteCount} viewed quotes</strong>
                      <p>
                        Customers have viewed these quotes and are yet to
                        approve them
                      </p>
                    </div>
                  </div>
                  <em className="status-pill status-viewed">Viewed</em>
                  <Link to="/quotations?activityStatus=Awaiting%20decision">
                    Follow up
                  </Link>
                </div>
                <div className="dashboard-attention-row">
                  <div className="dashboard-attention-context">
                    <LuFileText />
                    <div>
                      <strong>
                        {attentionInvoices.draftCount} unsent draft invoices ·{" "}
                        {money(attentionInvoices.draftAmount, currency)}
                      </strong>
                      <p>
                        Ready for review · Not included in your to receive
                        balance
                      </p>
                    </div>
                  </div>
                  <em className="status-pill status-draft">Draft</em>
                  <Link to="/invoices?tab=draft">Review drafts</Link>
                </div>
              </>
            )}
          </section>
          <section className="dashboard-charts">
            <article className="dashboard-card">
              <header>
                <h2>Receivables by due date</h2>
                <p>Historical snapshot · {formatDate(now)} · AUD thousands</p>
              </header>
              {dashboardLoading ? (
                <ChartSkeleton />
              ) : (
                <ReceivablesChart values={receivables} />
              )}
            </article>
            <article className="dashboard-card">
              <header>
                <h2>Cash in vs cash out</h2>
                <p>Historical · Last six months · AUD thousands</p>
              </header>
              {dashboardLoading ? (
                <ChartSkeleton />
              ) : (
                <CashChart incoming={cashIn} outgoing={cashOut} />
              )}
            </article>
          </section>
          <RecentInvoices
            rows={invoiceRows}
            currency={currency}
            loading={dashboardLoading}
          />
          <footer className="dashboard-mobile-plan">
            <strong>{planName}</strong>
            <span>
              Invoices {invoiceUsage} / {invoiceLimit || "—"}
            </span>
            <Link to="/subscriptions">Upgrade</Link>
          </footer>
        </main>
      </div>
      <CreateInvoiceModal
        open={createInvoiceModalOpen}
        onClose={() => setCreateInvoiceModalOpen(false)}
        onSelectDirect={() => {
          setCreateInvoiceModalOpen(false);
          navigate("/invoices/add?type=new");
        }}
        onSelectQuotation={(quoteId, quote) => {
          setCreateInvoiceModalOpen(false);
          navigate(`/invoices/add?quotationID=${quoteId}`, {
            state: {
              quotationID: quoteId,
              quotationNumber: quote?.quotationNumber,
              quotation: quote,
            },
          });
        }}
        businessUserID={user.businessUserID}
        darkMode={controller.darkMode}
      />
    </div>
  );
}
